/**
 * Invented pending invites for the Meridian Associates mockup tenant — the
 * "Pending invites" section on `/settings/access` (task 104d.1).
 *
 * Each invite row is what 104b's invite lifecycle surface will persist:
 * email + intended role + optional pre-filled Employee link + expiry.
 *
 * Coverage (per the 104d.1 contract):
 *   - 3 pending (within the 7-day TTL window)
 *   - 1 expired (expiresAt in the past — surfaces the Expired badge)
 *
 * All timestamps derive from FROZEN_NOW_MS — no new Date() calls.
 * All data is fictional.
 */

import { FROZEN_NOW_MS } from '@/lib/frozenTime';
import type { KanjoyUserRole } from './kanjoyUsers';

const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;

export interface PendingInvite {
  id: string;
  email: string;
  role: KanjoyUserRole;
  /** Pre-filled Employee link applied on acceptance (Q5D) */
  employeeId: string | null;
  /** ISO timestamp; rows where expiresAt < now render as "Expired" */
  expiresAt: string;
  createdAt: string;
  /** Display name of the Kanjoy user who sent the invite */
  createdBy: string;
}

const relative = (ms: number): string => new Date(FROZEN_NOW_MS + ms).toISOString();

export const PENDING_INVITES: readonly PendingInvite[] = [
  {
    id: 'inv-fresh-jordan',
    email: 'jordan.reyes@firm.com',
    role: 'member',
    employeeId: null,
    createdAt: relative(-2 * HOUR_MS),
    // Invite sent 2h ago; 7-day TTL leaves ~6d 22h remaining.
    expiresAt: relative(7 * DAY_MS - 2 * HOUR_MS),
    createdBy: 'Sarah Chen',
  },
  {
    id: 'inv-mid-nora',
    email: 'nora.mwangi@firm.com',
    role: 'admin',
    employeeId: null,
    createdAt: relative(-2 * DAY_MS),
    expiresAt: relative(5 * DAY_MS),
    createdBy: 'Priya Desai',
  },
  {
    id: 'inv-stale-ravi',
    email: 'ravi.shankar@firm.com',
    role: 'member',
    // Pre-filled employee link — acceptance will link to emp-daniel-kim.
    // Demonstrates the "link-on-acceptance" path (Q5D).
    employeeId: 'emp-daniel-kim',
    createdAt: relative(-5 * DAY_MS),
    expiresAt: relative(2 * DAY_MS),
    createdBy: 'Sarah Chen',
  },
  // ── Expired (1) ────────────────────────────────────────────────────────
  {
    id: 'inv-expired-taro',
    email: 'taro.yamashita@firm.com',
    role: 'member',
    employeeId: null,
    createdAt: relative(-10 * DAY_MS),
    expiresAt: relative(-3 * DAY_MS),
    createdBy: 'Marcus Rivera',
  },
];
